/// Demo hesabın verisi. Backend henüz yok (CLAUDE.md → Durum), dashboard
/// bu dosyayla besleniyor.
///
/// ---------------------------------------------------------------------------
/// Neden rastgele değil, TOHUMLU
/// ---------------------------------------------------------------------------
/// Sayılar `Math.random()` ile üretilseydi sunucu bir dizi, tarayıcı başka bir
/// dizi hesaplar ve React hidrasyonda uyuşmazlık verirdi. Buradaki üreteç
/// (mulberry32) sabit tohumdan aynı diziyi üretiyor — sunucuda ve tarayıcıda
/// birebir aynı sayı. Aynı sebeple tarih de `new Date()` ile değil sabit bir
/// ÇAPADAN türüyor: "bugün" sunucu saatiyle tarayıcı saati arasında gün
/// değiştirebilir.
///
/// ---------------------------------------------------------------------------
/// Veri gerçekten tutarlı
/// ---------------------------------------------------------------------------
/// Kampanya satırlarının harcaması, kanal kırılımının toplamı ve üstteki
/// özet kartı AYNI seriden türüyor. Uydurma veriyi kart kart yazmak kolaydı
/// ama "kampanyaların toplamı üstteki rakamı tutmuyor" ilk bakışta görülür ve
/// tüm paneli inandırıcılıktan düşürür.
///
/// Backend gelince: `getOverview()` ve `getCampaigns()` imzalarını koruyup
/// içlerini `api.get('/metrics/overview?range=30')` ile değiştirmek yeterli.

export const DEMO_EMAIL = 'demo@mentorivo.com';
export const DEMO_PASSWORD = 'demo1234';

/** Serinin bittiği gün. Sabit — bkz. dosya başlığı. */
const ANCHOR = Date.UTC(2026, 7, 10); // 10 Ağustos 2026
/** 90 günlük aralığın bir de ÖNCEKİ dönemi olmalı: 90 + 90. */
const TOTAL_DAYS = 180;

export const demoAccount = {
  user: {
    name: 'Deniz Aksoy',
    email: DEMO_EMAIL,
    initials: 'DA',
  },
  workspace: {
    name: 'Northwind Coffee',
    /** Üst çubuktaki workspace işareti — logo yok, baş harfler var. */
    initials: 'NC',
    /** Panel gerçek bir reklam hesabına bağlıymış gibi görünmesin diye. */
    demo: true,
    plan: 'growth',
    trialDaysLeft: 12,
    currency: 'USD',
  },
} as const;

export type RangeKey = 7 | 30 | 90;
export const RANGES: readonly [RangeKey, RangeKey, RangeKey] = [7, 30, 90];

export type MetricKey = 'spend' | 'conversions' | 'roas';
export type ChannelKey = 'meta' | 'google' | 'tiktok';
export const CHANNELS: readonly [ChannelKey, ChannelKey, ChannelKey] = [
  'meta',
  'google',
  'tiktok',
];

/* -------------------------------------------------------------------------- */
/* Üreteç                                                                      */
/* -------------------------------------------------------------------------- */

/** mulberry32 — 32 bitlik tohumdan deterministik 0..1 dizisi. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** ISO 'YYYY-MM-DD'. UTC ile: yerel saat diliminden bağımsız. */
function isoDay(offsetFromAnchor: number): string {
  const d = new Date(ANCHOR + offsetFromAnchor * 86_400_000);
  const iso = d.toISOString();
  return iso.slice(0, 10);
}

/* -------------------------------------------------------------------------- */
/* Günlük seri                                                                 */
/* -------------------------------------------------------------------------- */

export type ChannelDay = {
  spend: number;
  revenue: number;
  conversions: number;
};

export type DayPoint = {
  /** 'YYYY-MM-DD' */
  date: string;
  spend: number;
  revenue: number;
  conversions: number;
  impressions: number;
  clicks: number;
  byChannel: Record<ChannelKey, ChannelDay>;
};

/** Kanal karakteri: pay, ROAS ve sepet ortalaması birbirinden farklı olsun. */
const CHANNEL_PROFILE: Record<ChannelKey, { share: number; roas: number; aov: number }> = {
  meta: { share: 0.52, roas: 3.55, aov: 64 },
  google: { share: 0.33, roas: 4.4, aov: 78 },
  tiktok: { share: 0.15, roas: 2.65, aov: 49 },
};

function buildSeries(): DayPoint[] {
  const rnd = seeded(20260810);
  const points: DayPoint[] = [];

  for (let i = TOTAL_DAYS - 1; i >= 0; i -= 1) {
    const offset = -i;
    const date = isoDay(offset);
    /* Haftanın günü: hafta sonu reklam harcaması düşer, dönüşüm oranı artar —
       gerçek e-ticaret hesaplarının klasik deseni. Çapa UTC olduğu için gün
       de UTC'den okunuyor. */
    const weekday = new Date(ANCHOR + offset * 86_400_000).getUTCDay();
    const weekend = weekday === 0 || weekday === 6;

    /* Yavaş yükselen taban: 180 gün boyunca %38 büyüme. Panelin "işler
       yolunda" hikâyesi buradan geliyor, kartlardaki artı işaretlerinden
       değil. */
    const trend = 1 + ((TOTAL_DAYS - 1 - i) / (TOTAL_DAYS - 1)) * 0.38;
    const seasonal = weekend ? 0.82 : 1 + (weekday === 3 ? 0.06 : 0);
    const noise = 0.9 + rnd() * 0.2;

    const spend = 1180 * trend * seasonal * noise;

    const byChannel = {} as Record<ChannelKey, ChannelDay>;
    let revenue = 0;
    let conversions = 0;

    for (const channel of CHANNELS) {
      const profile = CHANNEL_PROFILE[channel];
      /* Payların toplamı 1; sapma ±%6 ile sınırlı ki kırılım kaymasın. */
      const share = profile.share * (0.94 + rnd() * 0.12);
      const cSpend = spend * share;
      /* ROAS'ın da yavaş bir yükselişi var (180 günde +%18). Olmasaydı iki
         dönem arasındaki fark yalnızca gürültüden gelir ve özet kartı her
         aralıkta "%0,1" gösterirdi — ölçtüğü şeyi ölçmüyor gibi durur. */
      const roasTrend = 1 + ((TOTAL_DAYS - 1 - i) / (TOTAL_DAYS - 1)) * 0.18;
      const cRoas = profile.roas * roasTrend * (weekend ? 1.08 : 1) * (0.88 + rnd() * 0.24);
      const cRevenue = cSpend * cRoas;
      const cConversions = cRevenue / (profile.aov * (0.95 + rnd() * 0.1));

      byChannel[channel] = {
        spend: cSpend,
        revenue: cRevenue,
        conversions: cConversions,
      };
      revenue += cRevenue;
      conversions += cConversions;
    }

    /* Payların ±%6 sapması yüzünden kanal harcamaları toplamı günlük tabanla
       birebir aynı değil. Gösterilen toplam KANALLARDAN toplanıyor, tabandan
       değil — kırılımın toplamı üstteki rakamı tutsun diye. */
    const spendActual = CHANNELS.reduce((sum, c) => sum + byChannel[c].spend, 0);

    const cpm = 8.4 * (0.92 + rnd() * 0.16);
    const impressions = (spendActual / cpm) * 1000;
    const ctr = 0.0185 * (0.9 + rnd() * 0.2);

    points.push({
      date,
      spend: spendActual,
      revenue,
      conversions,
      impressions,
      clicks: impressions * ctr,
      byChannel,
    });
  }

  return points;
}

const SERIES = buildSeries();

/* -------------------------------------------------------------------------- */
/* Toplamlar                                                                   */
/* -------------------------------------------------------------------------- */

export type Totals = {
  spend: number;
  revenue: number;
  conversions: number;
  impressions: number;
  clicks: number;
  /** Türetilmiş: gelir / harcama */
  roas: number;
  /** Türetilmiş: harcama / dönüşüm */
  cpa: number;
  /** Türetilmiş: tık / gösterim */
  ctr: number;
};

function sum(points: readonly DayPoint[]): Totals {
  const acc = points.reduce(
    (a, p) => ({
      spend: a.spend + p.spend,
      revenue: a.revenue + p.revenue,
      conversions: a.conversions + p.conversions,
      impressions: a.impressions + p.impressions,
      clicks: a.clicks + p.clicks,
    }),
    { spend: 0, revenue: 0, conversions: 0, impressions: 0, clicks: 0 },
  );

  return {
    ...acc,
    roas: acc.spend === 0 ? 0 : acc.revenue / acc.spend,
    cpa: acc.conversions === 0 ? 0 : acc.spend / acc.conversions,
    ctr: acc.impressions === 0 ? 0 : acc.clicks / acc.impressions,
  };
}

export type ChannelSlice = {
  channel: ChannelKey;
  spend: number;
  revenue: number;
  conversions: number;
  roas: number;
  /** Dönem harcamasındaki payı (0..1) */
  share: number;
  /** Önceki döneme göre harcama değişimi (0..1 oranında, null = bölen sıfır) */
  delta: number | null;
};

export type Overview = {
  range: RangeKey;
  points: readonly DayPoint[];
  totals: Totals;
  previous: Totals;
  channels: readonly ChannelSlice[];
};

function change(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

/**
 * Seçili aralığın verisi + karşılaştırma için hemen ÖNCESİNDEKİ eşit uzunlukta
 * dönem. "Geçen aya göre" değil "önceki 30 güne göre" — reklam panellerinin
 * standardı ve takvim ayı uzunluğu değişmediği için karşılaştırma dürüst.
 */
export function getOverview(range: RangeKey): Overview {
  const points = SERIES.slice(SERIES.length - range);
  const prevPoints = SERIES.slice(SERIES.length - range * 2, SERIES.length - range);

  const totals = sum(points);
  const previous = sum(prevPoints);

  const channels = CHANNELS.map((channel): ChannelSlice => {
    const spend = points.reduce((s, p) => s + p.byChannel[channel].spend, 0);
    const revenue = points.reduce((s, p) => s + p.byChannel[channel].revenue, 0);
    const conversions = points.reduce((s, p) => s + p.byChannel[channel].conversions, 0);
    const prevSpend = prevPoints.reduce((s, p) => s + p.byChannel[channel].spend, 0);

    return {
      channel,
      spend,
      revenue,
      conversions,
      roas: spend === 0 ? 0 : revenue / spend,
      share: totals.spend === 0 ? 0 : spend / totals.spend,
      delta: change(spend, prevSpend),
    };
  });

  return { range, points, totals, previous, channels };
}

/* -------------------------------------------------------------------------- */
/* Kampanyalar                                                                 */
/* -------------------------------------------------------------------------- */

export type CampaignStatus = 'active' | 'learning' | 'paused' | 'review';

type CampaignSeed = {
  id: string;
  name: string;
  channel: ChannelKey;
  status: CampaignStatus;
  /** Dönem harcamasındaki payı. Toplamları 1 — bkz. NORMALIZE aşağıda. */
  weight: number;
  /** Kampanyanın kendi ROAS'ı; kanal ortalamasından sapabilir. */
  roas: number;
  /** Günlük bütçe (USD) */
  dailyBudget: number;
};

/* ⚠️ DEMO VERİ. İsimler ve markalar uydurma; "Northwind Coffee" hayalî bir
   kahve markası. Yayına çıkmadan önce bu dizi gerçek API'den gelmeli. */
const CAMPAIGN_SEEDS: readonly CampaignSeed[] = [
  {
    id: 'c-prospect-coldbrew',
    name: 'Prospecting — Cold Brew Set',
    channel: 'meta',
    status: 'active',
    weight: 0.26,
    roas: 3.9,
    dailyBudget: 420,
  },
  {
    id: 'c-search-brand',
    name: 'Search — Brand',
    channel: 'google',
    status: 'active',
    weight: 0.18,
    roas: 7.2,
    dailyBudget: 260,
  },
  {
    id: 'c-retarget-cart',
    name: 'Retargeting — Cart Abandon',
    channel: 'meta',
    status: 'active',
    weight: 0.16,
    roas: 5.4,
    dailyBudget: 240,
  },
  {
    id: 'c-pmax-subscription',
    name: 'PMax — Subscription',
    channel: 'google',
    status: 'learning',
    weight: 0.15,
    roas: 2.8,
    dailyBudget: 300,
  },
  {
    id: 'c-ugc-creators',
    name: 'UGC Creators — Q3',
    channel: 'tiktok',
    status: 'active',
    weight: 0.13,
    roas: 2.6,
    dailyBudget: 210,
  },
  {
    id: 'c-lookalike-gift',
    name: 'Lookalike 3% — Gift Sets',
    channel: 'meta',
    status: 'review',
    weight: 0.08,
    roas: 1.9,
    dailyBudget: 180,
  },
  {
    id: 'c-shopping-bestseller',
    name: 'Shopping — Best Sellers',
    channel: 'google',
    status: 'paused',
    weight: 0.04,
    roas: 3.1,
    dailyBudget: 150,
  },
];

export type CampaignRow = {
  id: string;
  name: string;
  channel: ChannelKey;
  status: CampaignStatus;
  dailyBudget: number;
  spend: number;
  conversions: number;
  roas: number;
  /** Önceki döneme göre ROAS değişimi */
  delta: number | null;
  /** Kart içi mini grafik için normalize edilmiş 14 nokta (0..1) */
  trend: readonly number[];
};

/**
 * Kampanya satırları dönem toplamından TÜRETİLİYOR: payların toplamı 1 olduğu
 * için satırların harcaması üstteki toplamı birebir tutuyor.
 */
export function getCampaigns(range: RangeKey): readonly CampaignRow[] {
  const { totals } = getOverview(range);
  const weightSum = CAMPAIGN_SEEDS.reduce((s, c) => s + c.weight, 0);

  return CAMPAIGN_SEEDS.map((seed, index) => {
    const rnd = seeded(1000 + index * 37);
    const spend = totals.spend * (seed.weight / weightSum);
    const revenue = spend * seed.roas;
    /* Sepet ortalaması kanaldan geliyor; dönüşüm sayısı gelirden türüyor ki
       ROAS ile dönüşüm birbiriyle çelişmesin. */
    const conversions = revenue / CHANNEL_PROFILE[seed.channel].aov;

    /* Kampanyanın ROAS değişimi.
       Hesabın genel eğilimine bağlanmıyor: gerçek bir hesapta toplam yatay
       dururken tek tek kampanyalar birbirinden bağımsız oynar, hatta ters
       yönlere gider. Toplamdan türetilseydi yedi satır da aynı yönde ve
       aynı büyüklükte (ölçüldü: %0,1) çıkıyordu — tablo ölü görünüyordu.

       Yön durumla tutarlı: duraklatılan ve incelemeye alınan kampanyalar
       düşüşte. Rozetin kırmızısıyla durum etiketi birbirini doğruluyor. */
    const falling = seed.status === 'paused' || seed.status === 'review';
    const swing = (falling ? -0.2 : 0.09) + (rnd() - 0.5) * 0.34;
    const prevRoas = seed.roas / (1 + swing);

    const trendRnd = seeded(2000 + index * 91);
    const trend = Array.from({ length: 14 }, (_, i) => {
      const drift = i / 13;
      return 0.35 + drift * 0.35 + trendRnd() * 0.3;
    });

    return {
      id: seed.id,
      name: seed.name,
      channel: seed.channel,
      status: seed.status,
      dailyBudget: seed.dailyBudget,
      spend,
      conversions,
      roas: seed.roas,
      delta: change(seed.roas, prevRoas),
      trend,
    };
  });
}

/* -------------------------------------------------------------------------- */
/* AI ajan                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Ajanın yaptığı işler ve bekleyen önerileri.
 *
 * Metin BURADA DEĞİL: her kaydın `id`'si `messages/*.json` içindeki
 * `dashboard.agent.log.<id>` / `dashboard.agent.tips.<id>` anahtarına
 * karşılık geliyor, `values` de o cümlenin yerine geçen parçaları taşıyor.
 * Böylece aynı veri iki dilde de doğru cümle kuruyor.
 *
 * Zaman `new Date()` ile değil DAKİKA CİNSİNDEN UZAKLIK olarak duruyor —
 * "14 dk önce" etiketi sunucuda ve tarayıcıda aynı çıksın diye.
 */
export type AgentEntry = {
  id: string;
  minutesAgo: number;
  tone: 'positive' | 'neutral' | 'warning';
  values: Record<string, string | number>;
};

export const agentLog: readonly AgentEntry[] = [
  {
    id: 'budgetShift',
    minutesAgo: 14,
    tone: 'positive',
    values: { amount: 420, from: 'TikTok', to: 'Meta' },
  },
  {
    id: 'creativePaused',
    minutesAgo: 96,
    tone: 'neutral',
    values: { creative: 'Cold Brew — UGC 03', ctr: 0.006 },
  },
  {
    id: 'audienceExpanded',
    minutesAgo: 320,
    tone: 'positive',
    values: { campaign: 'Lookalike 3% — Gift Sets', from: 3, to: 5 },
  },
  {
    id: 'bidCapRaised',
    minutesAgo: 1450,
    tone: 'warning',
    values: { campaign: 'Search — Brand', amount: 1.4 },
  },
];

export type AgentTip = {
  id: string;
  /** Ajanın kendi güveni (0..1) — onay isteyen her öneri bunu gösterir. */
  confidence: number;
  values: Record<string, string | number>;
};

export const agentTips: readonly AgentTip[] = [
  {
    id: 'scaleWinner',
    confidence: 0.86,
    values: { campaign: 'Search — Brand', amount: 250, roas: 7.2 },
  },
  {
    id: 'pauseLoser',
    confidence: 0.74,
    values: { campaign: 'Lookalike 3% — Gift Sets', roas: 1.9 },
  },
  {
    id: 'refreshCreative',
    confidence: 0.68,
    values: { campaign: 'UGC Creators — Q3', days: 21 },
  },
];

/** Verinin ne kadar taze olduğu — üst çubuktaki etiket. */
export const dataFreshnessMinutes = 14;
